/**
 * The optional word list down the left of the screen (Settings > Word list).
 *
 * For fast typers who want to read ahead: every word in the level, small, with
 * a tick once a word is typed, a small cross for each wrong attempt, and a
 * line through anything finished. Off by default.
 *
 * Deliberately quiet. It sits under the animal and the word panel, and fades
 * back whenever either is over it, so it can never hide the thing the player
 * is actually watching - platforms are allowed as far left as x = 200, so on
 * some levels the animal really does pass behind it.
 */
import Phaser from 'phaser';
import {
  LIST_PAD,
  listPanelWidth,
  listWindow,
  MARK_GAP,
  MAX_CROSSES,
  OVERFLOW_W,
  WordLog,
} from '../../systems/wordLog';
import { FONT_FAMILY, GAME_HEIGHT } from '../../types';

/** Over the platforms (10), under the animal (20) and the word panel (30). */
export const WORD_LIST_DEPTH = 15;

const LEFT = 16;
const TOP = 152;
const ROW = 21;
const FONT_PX = 15;
const TEXT_X = LEFT + LIST_PAD;
const VISIBLE = Math.floor((GAME_HEIGHT - TOP - 14) / ROW);

const INK_CURRENT = '#F7FBFF';
const INK_AHEAD = '#C3D4E8';
const INK_DONE = '#7D8AA5';
const INK_SKIPPED = '#56627A';
const TICK = 0x2ec4b6;
const CROSS = 0xff8fa3;
const STRIKE = 0x7d8aa5;
const MARKER = 0xffc145;

const FADED_ALPHA = 0.22;

export class WordList {
  private readonly log: WordLog;
  private readonly container: Phaser.GameObjects.Container;
  private readonly backing: Phaser.GameObjects.Graphics;
  private readonly marks: Phaser.GameObjects.Graphics;
  /** Each word's drawn width, by row, measured once up front. */
  private readonly wordWidths: number[];
  private readonly backingHeight: number;
  private width = 0;
  private readonly words: Phaser.GameObjects.Text[] = [];
  private readonly extras: Phaser.GameObjects.Text[] = [];
  private faded = false;

  constructor(
    private readonly scene: Phaser.Scene,
    platforms: ReadonlyArray<{ readonly index: number; readonly word?: string }>,
    private readonly reducedMotion: boolean,
  ) {
    this.log = new WordLog(platforms);

    const rows = Math.min(VISIBLE, this.log.rows().length);
    this.backingHeight = rows * ROW + 14;

    // Sized to the words, not a fixed width: one throwaway Text measures them all.
    const probe = scene.add.text(0, 0, '', { fontFamily: FONT_FAMILY, fontSize: `${FONT_PX}px` });
    this.wordWidths = this.log.rows().map((row) => probe.setText(row.word).width);
    probe.destroy();

    this.backing = scene.add.graphics();
    this.marks = scene.add.graphics();
    this.container = scene.add.container(0, 0, [this.backing, this.marks]);
    this.resize();

    for (let i = 0; i < rows; i += 1) {
      const y = TOP + i * ROW;
      const word = scene.add
        .text(TEXT_X, y, '', {
          fontFamily: FONT_FAMILY,
          fontSize: `${FONT_PX}px`,
          color: INK_AHEAD,
        })
        .setOrigin(0, 0);
      const extra = scene.add
        .text(0, y + 1, '', { fontFamily: FONT_FAMILY, fontSize: '12px', color: INK_DONE })
        .setOrigin(0, 0);
      this.words.push(word);
      this.extras.push(extra);
      this.container.add([word, extra]);
    }

    this.container.setScrollFactor(0).setDepth(WORD_LIST_DEPTH);
  }

  /** The right-hand edge, so Game can tell when something is behind the list. */
  get right(): number {
    return LEFT + this.width;
  }

  hit(index: number, landing: number): void {
    this.log.hit(index, landing);
  }

  miss(index: number): void {
    this.log.miss(index);
  }

  /** Platform `index` was given a new word; call refresh() after. */
  replaceWord(index: number, word: string): void {
    this.log.replace(index, word);
    const position = this.log.positionOf(index);
    if (position < 0) return;
    const probe = this.scene.add.text(0, 0, word, {
      fontFamily: FONT_FAMILY,
      fontSize: `${FONT_PX}px`,
    });
    this.wordWidths[position] = probe.width;
    probe.destroy();
  }

  /** Redraw around the word for the platform after `currentIndex`. */
  refresh(currentIndex: number): void {
    const rows = this.log.rows();
    const current = this.log.positionOf(currentIndex + 1);
    const { start } = listWindow(rows.length, Math.max(0, current), this.words.length);

    this.resize();
    this.marks.clear();
    this.words.forEach((text, slot) => {
      const extra = this.extras[slot];
      const row = rows[start + slot];
      if (row === undefined) {
        text.setText('');
        extra?.setText('');
        return;
      }

      const isCurrent = start + slot === current;
      const color = isCurrent
        ? INK_CURRENT
        : row.done
          ? INK_DONE
          : row.skipped
            ? INK_SKIPPED
            : INK_AHEAD;
      text.setText(row.word).setColor(color);

      const y = TOP + slot * ROW;
      const mid = y + FONT_PX * 0.62;

      if (isCurrent) {
        this.marks.fillStyle(MARKER, 1);
        this.marks.fillRoundedRect(LEFT + 7, y + 3, 3, FONT_PX - 2, 1.5);
      }

      // A line through anything finished, so what is left stands out.
      if (row.done && !isCurrent) {
        this.marks.lineStyle(1.5, STRIKE, 0.9);
        this.marks.lineBetween(TEXT_X - 1, mid, TEXT_X + text.width + 1, mid);
      }

      // One small cross per wrong attempt, then a tick once it is right.
      let x = TEXT_X + text.width + MARK_GAP;
      const crosses = Math.min(row.misses, MAX_CROSSES);
      for (let i = 0; i < crosses; i += 1) {
        this.drawCross(x, mid);
        x += MARK_GAP;
      }
      if (row.misses > MAX_CROSSES) {
        extra?.setText(`+${row.misses - MAX_CROSSES}`).setPosition(x - 2, y + 2);
        x += OVERFLOW_W;
      } else {
        extra?.setText('');
      }
      if (row.done) this.drawTick(x + 1, mid);
    });
  }

  /**
   * Fade back while the animal or the word panel is over the list, so it never
   * competes with them. Called every frame; only acts when the state changes.
   */
  setFaded(faded: boolean): void {
    if (faded === this.faded) return;
    this.faded = faded;
    const alpha = faded ? FADED_ALPHA : 1;
    this.scene.tweens.killTweensOf(this.container);
    if (this.reducedMotion) {
      this.container.setAlpha(alpha);
      return;
    }
    this.scene.tweens.add({ targets: this.container, alpha, duration: 160, ease: 'Sine.easeOut' });
  }

  destroy(): void {
    this.container.destroy(true);
  }

  /**
   * Fit the backing to the longest word, with the same padding on the right as
   * on the left. Only redraws when a word's marks have pushed it wider.
   */
  private resize(): void {
    const entries = this.log.rows().map((row, i) => ({
      width: this.wordWidths[i] ?? 0,
      misses: row.misses,
      done: row.done,
    }));
    const width = Math.ceil(listPanelWidth(entries));
    if (width === this.width) return;
    this.width = width;
    this.backing.clear();
    this.backing.fillStyle(0x0b1220, 0.5);
    this.backing.fillRoundedRect(LEFT, TOP - 8, width, this.backingHeight, 10);
  }

  private drawCross(x: number, y: number): void {
    const r = 3;
    this.marks.lineStyle(1.75, CROSS, 1);
    this.marks.lineBetween(x - r, y - r, x + r, y + r);
    this.marks.lineBetween(x - r, y + r, x + r, y - r);
  }

  private drawTick(x: number, y: number): void {
    this.marks.lineStyle(2, TICK, 1);
    this.marks.beginPath();
    this.marks.moveTo(x - 3, y);
    this.marks.lineTo(x, y + 3);
    this.marks.lineTo(x + 5, y - 4);
    this.marks.strokePath();
  }
}
